import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  clearAccountingTransaction,
  clearAccountTransaction,
  clearAccountTransactionError,
  clearInitAccountStatus,
  requestAddMainBusiness,
  requestDeleteAcctTransaction,
  requestInitAccount,
  requestListAccount,
  requestListAccountTransaction,
  requestListBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../Context";
import {
  cancelAPIRequest,
  classNames,
  convertToTitleCase,
  getPeriodClosedError,
} from "../../Utils";
import PeriodLockBanner from "../../Components/v2/Accounting/PeriodLockBanner";
import PeriodLockCell from "../../Components/v2/Accounting/PeriodLockCell";
import PeriodClosedNotice from "../../Components/v2/Accounting/PeriodClosedNotice";
import { PencilAltIcon, TrashIcon, EyeIcon } from "@heroicons/react/outline";
import { NumericFormat } from "react-number-format";
import { useLocation, useNavigate } from "react-router-dom";
import { Pagination } from "../../Components/Pagination";
import { ModalAlert } from "../../Components/ModalAlert";
import { Alert } from "../../Components/Alert";
import TableSkeleton from "../../Components/TableSkeleton";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Badge from "../../Components/Badge";
import EmptyBusiness from "../../Components/EmptyBusiness";
import { EmptyBusinessSkeleton } from "../../Components/EmptyBusinessSkeleton";
import EmptyState from "../../Components/EmptyState";
import { Button } from "../../Components/Button";
import { TransactionFilter } from "../../Components/TransactionFilter";
import { dateFilterOptions } from "../../Constants";
import { isBusinessMainBlocked } from "../../Constants/business";
dayjs.extend(customParseFormat);

export const AccountTransactionList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const [selectedBusiness, setSelectedBusiness] = useState({});
  const [page, setpage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [transactionID, setTransactionID] = useState("");
  const [selectedAccount, setSelectedAccount] = useState({});
  const [date, setDate] = useState(new Date());
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [createdAt, setcreatedAt] = useState({});
  const [selectedFilter, setSelectedFilter] = useState(dateFilterOptions[0]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("businessID");
  const accountIdParam = searchParams.get("accountID");
  const dateParam = searchParams.get("date");

  const dispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();
  const authDispatch = useAuthDispatch();

  // Resolved from the selection the page already made, not from mainBusiness:
  // this route is routinely opened with an explicit ?businessID= for a different
  // business, and locking the wrong one is worse than not locking at all.
  const periodBusinessID = selectedBusiness?.value || selectedBusiness?.id;
  const { isDateClosed, refetch: refetchPeriod } =
    usePeriodLock(periodBusinessID);

  const { businesses, loading } = useBusinessTransactionState();
  const {
    request,
    nextTokens,
    submitLoading,
    submitError,
    deletedID,
    accounts,
    accountError,
    accountTransactions,
    accountTransactionsLoading,
    accountTransactionsError,
    initAccountLoading,
    initAccountStatus,
    allAccountLoading,
  } = useAccountingState();

  useEffect(() => {
    if (request) {
      cancelAPIRequest(request);
    }
    clearAccountTransaction(accountingDispatch);
    clearAccountTransactionError(accountingDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dateParam) {
      setDate(dayjs(dateParam).toDate());
      setDateRange([
        dayjs(dateParam).startOf("month").toDate(),
        dayjs(dateParam).endOf("month").toDate(),
      ]);
      setSelectedFilter({ value: "OTHER", label: "Other" });
    }
  }, [dateParam]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(dispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [dispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam,
          );
          if (business) {
            setSelectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setSelectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setSelectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setSelectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    // Function to set selected account
    const setSelectedAccountLogic = async () => {
      if (accountIdParam) {
        if (accounts.length > 0) {
          const account = accounts.find((item) => item.id === accountIdParam);
          if (account) {
            setSelectedAccount({
              ...account,
              label: account.name,
              value: account.id,
            });
          } else {
            // Handle if account is not found, select index 0
            setSelectedAccount({
              ...accounts[0],
              label: accounts[0].name,
              value: accounts[0].id,
            });
          }
        } else {
          // Handle if accounts are empty, no account to select
        }
      } else if (accounts.length > 0) {
        // Set selected account to the first item in the list
        setSelectedAccount({
          ...accounts[0],
          label: accounts[0].name,
          value: accounts[0].id,
        });
      } else {
        // Handle if accounts are empty, no account to select
      }
    };

    setSelectedAccountLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, accountIdParam]);

  useEffect(() => {
    if (selectedBusiness.value) {
      requestListAccount(accountingDispatch, {
        page: 1,
        businessID: selectedBusiness.value,
        loadAll: true,
      });
    }
  }, [accountingDispatch, selectedBusiness.value]);

  useEffect(() => {
    let start, end;

    if (selectedFilter.value === "MONTHLY") {
      start = dayjs(date).startOf("month");
      end = dayjs(date).endOf("month");
    } else if (selectedFilter.value === "OTHER") {
      if (!startDate || !endDate) return;
      start = dayjs(startDate?.toISOString());
      end = dayjs(endDate?.toISOString());
    }

    if (start && end) {
      setcreatedAt({
        between: [
          start.startOf("day").toISOString(),
          end.endOf("day").toISOString(),
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter.value, date, dateRange]);

  useEffect(() => {
    if (
      selectedBusiness?.id &&
      selectedAccount?.id &&
      Object.keys(createdAt).length > 0
    ) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, selectedAccount?.id, createdAt]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedID]);

  // Initialize accounts
  const handleInitAccount = () => {
    requestInitAccount(accountingDispatch, {
      businessID: selectedBusiness?.id,
    });
  };

  // Reload accounts after init success
  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(accountingDispatch, {
        businessID: selectedBusiness.id,
        loadAll: true,
      });
      clearInitAccountStatus(accountingDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  const onLoad = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (selectedBusiness?.id && selectedAccount?.id) {
      setpage(1);
      requestListAccountTransaction(accountingDispatch, {
        page: 1,
        limit: 10,
        accountID: selectedAccount.id,
        createdAt,
      });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListAccountTransaction(accountingDispatch, {
        page: nextPage,
        limit: 10,
        accountID: selectedAccount.id,
        nextToken: nextTokens[nextPage],
        createdAt,
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListAccountTransaction(accountingDispatch, {
        page: prevPage,
        limit: 10,
        accountID: selectedAccount.id,
        nextToken: nextTokens[prevPage],
        createdAt,
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListAccountTransaction(accountingDispatch, {
        page: selectedPage,
        accountID: selectedAccount.id,
        nextToken: nextTokens[selectedPage],
        createdAt,
      });
    }
  };

  const onUpdate = (transactionID, createdAtValue) => {
    if (isDateClosed(createdAtValue)) return;
    navigate(`/accounting-transaction/create?id=${transactionID}`);
  };

  const onDelete = () => {
    requestDeleteAcctTransaction(accountingDispatch, {
      id: transactionID,
    });
  };

  const EmptyAccount = () => {
    return initAccountLoading ? (
      <EmptyBusinessSkeleton />
    ) : accountError ? (
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-t-lg">
        <Alert
          title="Error"
          type="error"
          message={accountError?.message || "Error fetch accounting account."}
          errors={accountError?.errors}
        />
      </div>
    ) : (
      <EmptyState
        titleKey="No Accounts"
        descriptionKey="Please create an account first"
      >
        <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6">
          <Button
            outline
            className="flex-1"
            onClick={handleInitAccount}
            title="Create Automatically"
          />

          <Button
            outline
            className="flex-1"
            onClick={() =>
              navigate(`/account/import?id=${selectedBusiness.id}`)
            }
            title="Import Using XLSX File"
          />
        </div>
      </EmptyState>
    );
  };

  if (allAccountLoading || businesses.length === 0) {
    return <EmptyBusinessSkeleton />;
  }

  if (accounts.length === 0 && !allAccountLoading && selectedBusiness?.id) {
    return <EmptyAccount />;
  }

  if (user.mainBusinessID || businesses.length > 0) {
    return (
      <div className="space-y-4">
        <PeriodLockBanner businessID={periodBusinessID} />
        {
          <ModalAlert
            open={showConfirmModal}
            setOpen={(open) => {
              setShowConfirmModal(open);
            }}
            title="Delete Transaction"
            description="Are you sure you want to delete this accounting transaction?"
            confirmButtonText="Yes"
            cancelButtonText="Cancel"
            loadingButton={submitLoading}
            disabledButton={submitLoading}
            onConfirm={onDelete}
          />
        }
        {
          <ModalConfirm
            open={showSuccessModal}
            type="SUCCESS"
            setOpen={(open) => {
              setShowSuccessModal(open);
            }}
            title="Success"
            description="The accounting transaction has been successfully deleted"
            onConfirm={() => clearAccountingTransaction(accountingDispatch)}
          />
        }
        <TransactionFilter
          selectedBusiness={selectedBusiness}
          setSelectedBusiness={setSelectedBusiness}
          date={date}
          setDate={setDate}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
          dateRange={dateRange}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          createdAt={createdAt}
          onLoad={onLoad}
          filterDate="range"
          CustomFilterComponent={
            <Listbox value={selectedAccount} onChange={setSelectedAccount}>
              {({ open }) => (
                <>
                  <ListboxButton
                    className={classNames(
                      accounts.length === 0
                        ? "bg-gray-200 border-transparent"
                        : "bg-white",
                      "flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm",
                    )}
                  >
                    <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                      {selectedAccount?.id
                        ? `${selectedAccount?.code} - ${selectedAccount?.name}`
                        : t("accountName")}
                    </span>
                    {accounts.length === 0 ? (
                      <svg
                        role="status"
                        className="w-5 h-5 animate-spin text-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="#E5E7EB"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    )}
                  </ListboxButton>
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-auto">
                      {accounts.map((account) => (
                        <ListboxOption
                          key={account.id}
                          className={({ focus }) =>
                            classNames(
                              focus
                                ? "text-white bg-blue-600"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pl-3 pr-9",
                            )
                          }
                          value={account}
                        >
                          {({ selected, focus }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <span
                                  className={classNames(
                                    selected ? "font-semibold" : "font-normal",
                                    "ml-3 mr-2 block truncate",
                                  )}
                                >
                                  {`${account.code} - ${account.name}`}
                                </span>
                                {selected ? (
                                  <span
                                    className={classNames(
                                      focus ? "text-white" : "text-blue-600",
                                      "absolute inset-y-0 right-0 flex items-center pr-4",
                                    )}
                                  >
                                    <CheckIcon className="h-5 w-5" />
                                  </span>
                                ) : null}
                              </div>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </>
              )}
            </Listbox>
          }
        />
        <div className="flex flex-col">
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                {submitError && (
                  <div className="bg-white px-4 py-6 sm:px-6">
                    <PeriodClosedNotice
                      error={submitError}
                      onRefresh={refetchPeriod}
                    />
                    {!getPeriodClosedError(submitError) && (
                      <Alert
                        title="Error"
                        type="error"
                        message={
                          submitError?.message ||
                          "submitError delete Transaction."
                        }
                        errors={submitError?.errors}
                      />
                    )}
                  </div>
                )}
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Date")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Note")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Type")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                        {t("Total")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Action")}
                      </th>
                    </tr>
                  </thead>
                  {accountTransactionsLoading ? (
                    <TableSkeleton rows={3} columns={5} />
                  ) : (
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {selectedBusiness && accountTransactions?.length > 0 ? (
                        accountTransactions.map((accountTransaction) => {
                          const { transaction } = accountTransaction;
                          return (
                            <tr key={transaction.id}>
                              <th className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                                {dayjs(transaction.createdAt)
                                  .locale(i18n.language)
                                  .format("DD MMM YYYY")}
                              </th>
                              <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                                {transaction.description}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                <div className="flex gap-2">
                                  <div>
                                    {t(convertToTitleCase(transaction.type))}
                                  </div>
                                  {transaction.paid &&
                                    [
                                      "INCOME_RECEIVABLE",
                                      "EXPENSE_PAYABLE",
                                      "PAYABLE",
                                      "RECEIVABLE",
                                    ].includes(transaction.type) && (
                                      <Badge title={t("Paid")} type="success" />
                                    )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  displayType={"text"}
                                  value={transaction.total}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {isDateClosed(transaction.createdAt) ? (
                                  // One lock rather than two greyed-out icons.
                                  // Invoice rows keep their read-only route via
                                  // onView — viewing was never a write.
                                  <PeriodLockCell
                                    onView={
                                      transaction.invoiceNumber &&
                                      transaction?.items?.items
                                        ? () =>
                                            navigate(
                                              `/invoice/${transaction.id}`,
                                            )
                                        : undefined
                                    }
                                  />
                                ) : (
                                <div className="flex flex-auto gap-2">
                                  {transaction.invoiceNumber &&
                                  transaction?.items?.items ? (
                                    <button
                                      onClick={() => {
                                        navigate(`/invoice/${transaction.id}`);
                                      }}
                                    >
                                      <EyeIcon className="h-5 w-5 text-blue-500" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        onUpdate(
                                          transaction.id,
                                          transaction.createdAt,
                                        )
                                      }
                                    >
                                      <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                    </button>
                                  )}

                                  <button
                                    onClick={() => {
                                      setShowConfirmModal(true);
                                      setTransactionID(transaction.id);
                                    }}
                                  >
                                    <TrashIcon className="h-5 w-5 text-red-500" />
                                  </button>
                                </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <ErrorStateTable error={accountTransactionsError} />
                      )}
                    </tbody>
                  )}
                </table>
                <Pagination
                  page={page}
                  onPrev={onPrev}
                  onNext={onNext}
                  onPageChange={onPageChange}
                  nextTokens={nextTokens}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <EmptyBusiness loading={loading} />;
  }
};
